import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUserRepository } from '../user/useUserRepository';
import { SocialAuthShell } from './social-auth-shell';
import { useSocialMode } from './social-mode-context';
import type { BeginSocialSessionInput, SocialSessionGrant } from './use-social-session-repository';
import { useSocialSessionRepository } from './use-social-session-repository';
import { startSocialAuthorization } from './start-social-authorization';

function validEmail(value: string | null): string {
  return value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : '';
}

interface LoginResponse {
  data: {
    token: string;
  };
  status: number;
}

export interface SocialLoginProps {
  onAuthenticated?: (destination: string) => void;
}

function isLoginResponse(value: unknown): value is LoginResponse {
  if (typeof value !== 'object' || value === null || !('status' in value) || !('data' in value)) {
    return false;
  }

  const data = value.data;
  return value.status === 200
    && typeof data === 'object'
    && data !== null
    && 'token' in data
    && typeof data.token === 'string';
}

function redirectToSocialWeb(token: string): void {
  const socialWebUrl = process.env.SOCIAL || process.env.FRONT || 'http://localhost:3005';
  window.location.assign(`${socialWebUrl}/?t=${encodeURIComponent(token)}`);
}

function redirectTo(destination: string): void {
  window.location.assign(destination);
}

function handoffParameters(): Omit<BeginSocialSessionInput, 'password' | 'remember' | 'username'> | null {
  const params = new URLSearchParams(window.location.search);
  const attemptId = params.get('attempt_id');
  const callback = params.get('callback');
  const codeChallenge = params.get('code_challenge');
  const state = params.get('state');

  if (!attemptId || !callback || !codeChallenge || !state) {
    return null;
  }

  return { attemptId, callback, codeChallenge, state };
}

function callbackDestination(grant: SocialSessionGrant): string {
  const destination = new URL(grant.callback);
  destination.searchParams.set('code', grant.code);
  destination.searchParams.set('state', grant.state);
  destination.searchParams.set('attempt_id', grant.attemptId);

  return destination.toString();
}

function PasswordToggleIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      focusable="false"
      height="20"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="20"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function SocialLogin({ onAuthenticated }: SocialLoginProps) {
  const { login } = useUserRepository();
  const { beginSession } = useSocialSessionRepository();
  const { isSocialMode, intent, withSocialParam } = useSocialMode();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const needsAuthorization = isSocialMode && !handoffParameters();

  useEffect(() => {
    if (needsAuthorization) {
      startSocialAuthorization('login', intent);
    }
  }, [needsAuthorization, intent]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const email = formData.get('email');
    const password = formData.get('password');
    const remember = formData.get('remember') === 'on';

    if (typeof email !== 'string' || typeof password !== 'string') {
      setError('Completa el email y la contraseña.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      if (isSocialMode) {
        const handoff = handoffParameters();
        if (!handoff) {
          startSocialAuthorization('login', intent);
          return;
        }
        const grant = await beginSession({ ...handoff, username: email, password, remember, ...(intent ? { intent } : {}) });
        (onAuthenticated ?? redirectTo)(callbackDestination(grant));
        return;
      }

      const response: unknown = await login({ username: email, password });

      if (!isLoginResponse(response)) {
        setError('No hemos podido validar tus credenciales.');
        return;
      }

      if (onAuthenticated) {
        onAuthenticated(response.data.token);
      } else {
        redirectToSocialWeb(response.data.token);
      }
    } catch {
      setError('Usuario o contraseña incorrecta.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <SocialAuthShell
      claim="La confianza se construye con referencias."
      description="Descubre empresas, profesionales y servicios con reputación verificable. Deja y recibe referencias que abren oportunidades."
      footer={<p>¿No tienes cuenta? <Link to={withSocialParam('/register')}>Crear una cuenta</Link></p>}
      subtitle="Accede a tu reputación profesional."
      title="Inicia sesión con tu correo"
    >
      {isSocialMode && intent ? <p role="status">Inicia sesión para terminar tu referencia.</p> : null}
      <form className="or-social-auth__form" onSubmit={handleSubmit}>
        <label className="or-social-auth__field">
          <span className="or-social-auth__field-label">Correo electrónico</span>
          <input autoComplete="email" className="or-social-auth__input" defaultValue={isSocialMode ? validEmail(new URLSearchParams(window.location.search).get('email')) : ''} inputMode="email" name="email" placeholder="tucorreo@dominio.com" required type="email" />
        </label>
        <label className="or-social-auth__field">
          <span className="or-social-auth__field-label">Contraseña</span>
          <span className="or-social-auth__password">
            <input autoComplete="current-password" className="or-social-auth__input or-social-auth__input--with-icon" minLength={8} name="password" placeholder="Tu contraseña" required type={showPassword ? 'text' : 'password'} />
            <button aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'} aria-pressed={showPassword} className="or-social-auth__password-toggle" onClick={() => setShowPassword((visible) => !visible)} type="button">
              <PasswordToggleIcon />
            </button>
          </span>
        </label>
        <div className="or-social-auth__row">
          <label className="or-social-auth__remember">
            <input className="or-social-auth__checkbox" defaultChecked name="remember" type="checkbox" />
            <span className="or-social-auth__remember-text">Mantener sesión iniciada</span>
          </label>
          <Link to={withSocialParam('/retrieve')}>¿Olvidaste tu contraseña?</Link>
        </div>
        {error ? <p className="or-social-auth__error" role="alert">{error}</p> : null}
        {needsAuthorization ? <p role="status">Preparando acceso seguro…</p> : null}
        <button className="or-social-auth__submit" disabled={isSubmitting || needsAuthorization} type="submit">{isSubmitting ? 'Iniciando sesión…' : 'Iniciar sesión'}</button>
      </form>
    </SocialAuthShell>
  );
}
